import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { EditButton } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import { attributeReorder } from '../../actions/accountActions';

const styles = theme => ({
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        height: 56,
        color: theme.palette.text.secondary,
        fontSize: theme.typography.pxToRem(12),
        fontWeight: theme.typography.fontWeightMedium,
        fontFamily: theme.typography.fontFamily,
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
    },
    bodyRow: {
        display: 'flex',
        alignItems: 'center',
        height: 48,
        color: theme.palette.text.primary,
        fontSize: theme.typography.pxToRem(13),
        fontFamily: theme.typography.fontFamily,
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    bodyRowDragging: {
        display: 'flex',
        alignItems: 'center',
        height: 48,
        color: theme.palette.text.primary,
        fontSize: theme.typography.pxToRem(13),
        fontFamily: theme.typography.fontFamily,
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        backgroundColor: theme.palette.action.hover,
        boxShadow: theme.shadows[3],
    },
    handleCell: {
        padding: '4px 0 4px 12px',
        flexShrink: 0,
        width: 40,
        color: 'rgba(0, 0, 0, 0.26)',
        cursor: 'grab',
        display: 'flex',
        alignItems: 'center',
    },
    cell: {
        padding: '4px 24px 4px 24px',
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    actionCell: {
        padding: '4px 24px 4px 0',
        flexShrink: 0,
        minWidth: 100,
    },
    checkboxCell: {
        padding: '0 4px',
        flexShrink: 0,
        width: 58,
        display: 'flex',
        alignItems: 'center',
    },
});

const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const AttributeDraggableList = ({ ids = [], data = {}, basePath, hasEdit, loadedOnce, hasBulkActions, selectedIds = [], onSelect, onToggleItem, dispatch, classes }) => {
    const { t } = useTranslation();
    const [orderedIds, setOrderedIds] = useState([]);

    useEffect(() => {
        if (!ids || ids.length === 0) return;
        setOrderedIds(ids);
    }, [ids]);

    const onDragEnd = (result) => {
        if (!result.destination || result.destination.index === result.source.index) return;
        const newOrder = reorder(orderedIds, result.source.index, result.destination.index);
        setOrderedIds(newOrder);
        dispatch(attributeReorder(newOrder));
    };

    if (!loadedOnce || orderedIds.length === 0) return null;

    return (
        <div>
            <div className={classes.headerRow}>
                <div className={classes.handleCell}/>
                {hasBulkActions && (
                    <div className={classes.checkboxCell}>
                        <Checkbox
                            color="primary"
                            checked={
                                selectedIds.length > 0 &&
                                orderedIds.length > 0 &&
                                !orderedIds.find(id => selectedIds.indexOf(id) === -1)
                            }
                            onChange={(e) => e.target.checked ? onSelect(orderedIds) : onSelect([])}
                        />
                    </div>
                )}
                <div className={classes.cell}>{t('Name')}</div>
                <div className={classes.cell}>{t('Slug')}</div>
                <div className={classes.cell}>{t('Type')}</div>
                <div className={classes.cell}>{t('Created')}</div>
                {hasEdit && <div className={classes.actionCell}/>}
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="attributes-list">
                    {(droppableProvided) => (
                        <div
                            ref={droppableProvided.innerRef}
                            {...droppableProvided.droppableProps}
                        >
                            {orderedIds.map((id, index) => {
                                const record = data[id];
                                if (!record) return null;
                                return (
                                    <Draggable key={String(id)} draggableId={String(id)} index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className={snapshot.isDragging ? classes.bodyRowDragging : classes.bodyRow}
                                                style={provided.draggableProps.style}
                                            >
                                                <div className={classes.handleCell} {...provided.dragHandleProps}>
                                                    <DragHandleIcon fontSize="default"/>
                                                </div>
                                                {hasBulkActions && (
                                                    <div className={classes.checkboxCell}>
                                                        <Checkbox
                                                            color="primary"
                                                            checked={selectedIds.indexOf(id) !== -1}
                                                            onChange={() => onToggleItem(id)}
                                                        />
                                                    </div>
                                                )}
                                                <div className={classes.cell}>{record.name}</div>
                                                <div className={classes.cell}>{record.slug}</div>
                                                <div className={classes.cell}>{record.type}</div>
                                                <div className={classes.cell}>
                                                    {record.createdAt || ''}
                                                </div>
                                                {hasEdit && (
                                                    <div className={classes.actionCell}>
                                                        <EditButton basePath={basePath} record={record}/>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {droppableProvided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
};

export default connect()(withStyles(styles)(AttributeDraggableList));
